import { supabase } from '@/integrations/supabase/client';
import { AdaptiveCompressionService } from './adaptiveCompressionService';
import { ImageCompressionService } from './imageCompressionService';
import { ImageValidationService } from './imageValidationService';
import { IntelligentImageCache } from './intelligentImageCache';
import { StorageMonitoringService } from './storageMonitoringService';
import { uploadCleanupService } from './uploadCleanupService';
import { UploadRateLimiter } from './uploadRateLimiter';
import { ImageUrlService } from './imageUrlService';

// Environment variables for upload
const UPLOAD_API_URL = import.meta.env.VITE_UPLOAD_API_URL || 'http://localhost:4000/api/upload/avatar';
const UPLOAD_TOKEN = import.meta.env.VITE_UPLOAD_TOKEN || 'default-token';


export interface UploadResponse {
  success: true;
  url: string;
  message?: string;
  file?: {
    filename: string;
    path: string;
    mimetype: string;
    size: number;
  };
  compressionStats?: {
    originalSize: number;
    compressedSize: number;
    compressionRatio: number;
  };
  validationWarnings?: string[];
}

export interface UploadError {
  success: false;
  error: string;
  allowed?: string[];
  rateLimitInfo?: {
    remaining: number;
    resetTime: number;
    isBlocked: boolean;
    blockReason?: string;
  };
}

export class UploadService {
  /**
   * Faz upload direto para o Supabase Storage
   * @param file - Arquivo de imagem
   * @param userId - ID do usuário
   * @param type - Tipo de usuário ('prestadores' ou 'clientes')
   * @returns Promise com URL da imagem ou erro
   */
  static async uploadAvatarToSupabase(
    file: File, 
    userId: string, 
    type: 'prestadores' | 'clientes'
  ): Promise<UploadResponse | UploadError> {
    try {
      console.log('📤 Upload direto para Supabase Storage...');
      
      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `${type}/${fileName}`;
      
      // Upload para Supabase Storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('❌ Erro no upload Supabase:', error);
        return { success: false, error: `Erro no upload: ${error.message}` };
      }

      // Obter URL pública
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      console.log('✅ Upload para Supabase concluído:', urlData.publicUrl);
      
      return {
        success: true,
        url: urlData.publicUrl,
        message: 'Upload realizado com sucesso',
        file: {
          filename: fileName,
          path: filePath,
          mimetype: file.type,
          size: file.size
        }
      };
      
    } catch (error) {
      console.error('💥 Erro inesperado no upload Supabase:', error);
      return { success: false, error: 'Erro interno no upload' };
    }
  }

  /**
   * Faz upload de avatar para o usuário com validações avançadas
   * @param file - Arquivo de imagem
   * @param userId - ID do usuário
   * @param type - Tipo de usuário ('prestadores' ou 'clientes')
   * @returns Promise com URL da imagem ou erro
   */
  static async uploadAvatar(
    file: File, 
    userId: string, 
    type: 'prestadores' | 'clientes'
  ): Promise<UploadResponse | UploadError> {
    try {
      // 1. Verificar rate limiting
      const rateLimitInfo = UploadRateLimiter.canUpload(userId, file.size);
      if (rateLimitInfo.isBlocked) {
        return {
          success: false,
          error: `Usuário bloqueado: ${rateLimitInfo.blockReason}`,
          rateLimitInfo
        };
      }

      if (rateLimitInfo.remaining <= 0) {
        return {
          success: false,
          error: `Limite de upload excedido. Tente novamente em ${Math.ceil((rateLimitInfo.resetTime - Date.now()) / 1000)} segundos`,
          rateLimitInfo
        };
      }

      // 2. Validação avançada de conteúdo
      console.log('🔍 Validando arquivo de imagem...');
      const validationResult = await ImageValidationService.validateImage(file);
      
      if (!validationResult.isValid) {
        // Registrar tentativa falhada
        UploadRateLimiter.recordUploadAttempt(userId, file.size, false);
        
        return {
          success: false,
          error: `Validação falhou: ${validationResult.errors.join(', ')}`,
          allowed: ['jpg', 'jpeg', 'png', 'webp']
        };
      }

            // 3. Compressão adaptativa baseada no dispositivo
      let fileToUpload = file;
      let compressionStats: any = undefined;

      try {
        console.log('📱 Aplicando compressão adaptativa...');
        const adaptiveResult = await AdaptiveCompressionService.compressAdaptively(file);
        
        fileToUpload = new File([adaptiveResult.blob], file.name, {
          type: `image/${adaptiveResult.profile.format}`,
          lastModified: Date.now()
        });
        
        compressionStats = {
          originalSize: adaptiveResult.originalSize,
          compressedSize: adaptiveResult.compressedSize,
          compressionRatio: adaptiveResult.compressionRatio,
          deviceInfo: adaptiveResult.deviceInfo,
          profile: adaptiveResult.profile
        };

        console.log(`✅ Compressão adaptativa aplicada: ${adaptiveResult.compressionRatio.toFixed(1)}% de redução`);
        console.log(`📱 Perfil: ${adaptiveResult.profile.optimization} para ${adaptiveResult.deviceInfo.type}`);
      } catch (compressionError) {
        console.warn('⚠️ Falha na compressão adaptativa, usando compressão padrão:', compressionError);
        
        // Fallback para compressão padrão
        if (ImageCompressionService.needsCompression(file)) {
          try {
            const fallbackResult = await ImageCompressionService.autoCompress(file);
            fileToUpload = new File([fallbackResult.blob], file.name, {
              type: `image/${fallbackResult.format}`,
              lastModified: Date.now()
            });
            
            compressionStats = {
              originalSize: fallbackResult.originalSize,
              compressedSize: fallbackResult.compressedSize,
              compressionRatio: fallbackResult.compressionRatio
            };

            console.log(`✅ Fallback: Imagem comprimida: ${fallbackResult.compressionRatio.toFixed(1)}% de redução`);
          } catch (fallbackError) {
            console.warn('⚠️ Fallback falhou, usando arquivo original:', fallbackError);
          }
        }
      }

      // 4. Upload direto para o Supabase Storage (bucket `avatars`, RLS por
      // path: 'clientes/{auth.uid()}_...' ou 'Prestadores/...' — ver migração
      // 20260719_avatars_clientes_policy.sql no repo admin).
      console.log('📤 Iniciando upload...');
      const uploadResult = await this.uploadAvatarToSupabase(fileToUpload, userId, type);

      if (!uploadResult.success) {
        UploadRateLimiter.recordUploadAttempt(userId, file.size, false);
        return uploadResult;
      }

      UploadRateLimiter.recordUploadAttempt(userId, file.size, true);

      // 5. Integração com cache local (best-effort, não bloqueia o upload)
      try {
        await IntelligentImageCache.addToCache(uploadResult.url, fileToUpload, 'high', {
          width: compressionStats?.deviceInfo?.screenWidth || 0,
          height: compressionStats?.deviceInfo?.screenHeight || 0,
          format: compressionStats?.profile?.format || 'unknown',
          originalSize: compressionStats?.originalSize || file.size,
          compressionRatio: compressionStats?.compressionRatio || 0
        });

        StorageMonitoringService.recordFileUpload(
          userId,
          fileToUpload.size,
          fileToUpload.type
        );
      } catch (integrationError) {
        console.warn('⚠️ Erro na integração com cache:', integrationError);
      }

      // 6. Limpeza automática desligada — ver nota em initializeServices().

      return {
        ...uploadResult,
        compressionStats,
        validationWarnings: validationResult.warnings
      };

    } catch (error) {
      console.error('Erro no upload:', error);
      
      // Registrar tentativa falhada
      UploadRateLimiter.recordUploadAttempt(userId, file.size, false);
      
      return { success: false, error: 'Erro interno no upload' };
    }
  }

  /**
   * Faz upload de arquivos para a galeria do prestador
   * @param file - O arquivo a ser enviado (imagem ou vídeo)
   * @param userId - O ID do prestador
   * @param type - Tipo de usuário ('prestadores' ou 'clientes')
   * @returns Uma Promise que resolve para UploadResponse em caso de sucesso ou UploadError em caso de falha
   */
  static async uploadGaleria(file: File, userId: string, type: 'prestadores' | 'clientes'): Promise<UploadResponse | UploadError> {
    try {
      console.log('🚀 Iniciando upload para galeria:', file.name);

      // Validações básicas
      if (!file) {
        return {
          success: false,
          error: 'Nenhum arquivo fornecido'
        };
      }

      // Verificar se é imagem ou vídeo
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        return {
          success: false,
          error: 'Tipo de arquivo não suportado. Apenas imagens e vídeos são permitidos.'
        };
      }

      // Validação de tamanho (10MB para galeria)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: `Arquivo muito grande. Tamanho máximo: ${(maxSize / 1024 / 1024).toFixed(1)}MB`
        };
      }

      // Validação de conteúdo usando ImageValidationService
      if (isImage) {
        const validationResult = await ImageValidationService.validateImage(file);
        if (!validationResult.isValid) {
          return {
            success: false,
            error: `Imagem inválida: ${validationResult.errors.join(', ')}`
          };
        }
      }

      // Rate limiting
      const rateLimitInfo = UploadRateLimiter.canUpload(userId, file.size);
      if (rateLimitInfo.isBlocked) {
        return {
          success: false,
          error: `Usuário bloqueado: ${rateLimitInfo.blockReason}`
        };
      }

      // Compressão automática para imagens grandes
      let processedFile = file;
      if (isImage && file.size > 2 * 1024 * 1024) { // 2MB
        console.log('🔄 Comprimindo imagem grande...');
        try {
          const compressionResult = await ImageCompressionService.autoCompress(file);
          processedFile = new File([compressionResult.blob], file.name, {
            type: `image/${compressionResult.format}`,
            lastModified: Date.now()
          });
        } catch (compressionError) {
          console.warn('⚠️ Falha na compressão, usando arquivo original:', compressionError);
        }
      }

      // Upload direto para o Supabase Storage (bucket `galeria`, publico) —
      // o endpoint antigo (VPS 213.199.44.151, desligada desde a migracao
      // para Vercel/Supabase) falhava sempre nesta chamada.
      const fileExt = processedFile.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `${type}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('galeria')
        .upload(filePath, processedFile, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        UploadRateLimiter.recordUploadAttempt(userId, file.size, false);
        return { success: false, error: `Erro no upload: ${uploadError.message}` };
      }

      const { data: urlData } = supabase.storage.from('galeria').getPublicUrl(filePath);

      // Registrar tentativa bem-sucedida
      UploadRateLimiter.recordUploadAttempt(userId, file.size, true);

      console.log('✅ Upload para galeria concluído:', urlData.publicUrl);
      return {
        success: true,
        url: urlData.publicUrl,
        message: 'Upload realizado com sucesso',
        file: {
          filename: fileName,
          path: filePath,
          mimetype: processedFile.type,
          size: processedFile.size
        }
      };

    } catch (error) {
      console.error('❌ Erro no upload para galeria:', error);
      return {
        success: false,
        error: 'Erro interno no upload para galeria'
      };
    }
  }

  /**
   * Remove avatar antigo se existir
   * @param currentUrl - URL atual do avatar
   * @param userId - ID do usuário
   */
  static async removeOldAvatar(currentUrl: string | null, userId: string): Promise<void> {
    if (!currentUrl) return;

    try {
      // URL do antigo servidor VPS (desligado desde a migração para
      // Vercel/Supabase) — não há nada a remover ali, ignorar.
      if (currentUrl.includes('213.199.44.151:4000/uploads/')) {
        return;
      }

      // Se for do Supabase Storage antigo, remover
      if (currentUrl.includes('supabase.co/storage/')) {
        const { error } = await supabase.storage
          .from('avatars')
          .remove([currentUrl.split('/object/public/')[1]]);
        
        if (error) {
          console.warn('Erro ao remover avatar antigo:', error);
        }
      }
    } catch (error) {
      console.warn('Erro ao remover avatar antigo:', error);
    }
  }

  /**
   * Valida se uma URL é válida
   * @param url - URL para validar
   * @returns true se válida
   */
  static isValidImageUrl(url: string): boolean {
    return ImageUrlService.isValidImageUrl(url);
  }

  /**
   * Obtém estatísticas de rate limiting para um usuário
   */
  static getRateLimitStats(userId: string) {
    return UploadRateLimiter.getUserStats(userId);
  }

  /**
   * Obtém estatísticas gerais do sistema
   */
  static getSystemStats() {
    return UploadRateLimiter.getSystemStats();
  }

  /**
   * Força limpeza de arquivos antigos
   */
  static async forceCleanup() {
    return uploadCleanupService.forceCleanup();
  }

  /**
   * Obtém estatísticas do cache inteligente
   */
  static getCacheStats() {
    return IntelligentImageCache.getStats();
  }

  /**
   * Obtém métricas de storage
   */
  static getStorageMetrics() {
    return StorageMonitoringService.getCurrentMetrics();
  }

  /**
   * Obtém alertas de storage
   */
  static getStorageAlerts() {
    return StorageMonitoringService.getActiveAlerts();
  }

  /**
   * Gera relatório completo de storage
   */
  static generateStorageReport() {
    return StorageMonitoringService.generateReport();
  }

  /**
   * Pré-carrega imagem no cache
   */
  static async preloadImage(url: string, priority: 'low' | 'medium' | 'high' | 'critical' = 'medium') {
    return IntelligentImageCache.preloadImage(url, priority);
  }

  /**
   * Agenda limpeza automática (não bloqueante)
   */
  // scheduleCleanup() removido: chamava uploadCleanupService.performCleanup()
  // 1s depois de cada upload — ver nota em initializeServices().

    /**
   * Inicia todos os serviços de background
   */
  static initializeServices(): void {
    if (import.meta.env.DEV) console.log('🚀 Inicializando serviços de upload...');

    // NÃO reativar uploadCleanupService/uploadBackupService sem os implementar
    // primeiro: as rotinas de "encontrar ficheiros órfãos/antigos/grandes" e de
    // listar ficheiros para backup nunca foram implementadas — os próprios
    // ficheiros dizem "Simular ..." e inventam resultados com Math.random().
    // Antes disto, corriam no browser de CADA visitante e liam as tabelas
    // profiles/prestadores/servicos por inteiro para depois comparar com uma
    // lista de ficheiros fictícia. Nada era realmente apagado, mas o custo das
    // leituras era real. (Os dois ficheiros já tinham o próprio auto-arranque
    // comentado no fim; era esta função que os voltava a ligar.)

    // Iniciar monitoramento de storage
    StorageMonitoringService.startMonitoring();

    // Configurar cache inteligente
    IntelligentImageCache.configure({
      maxSize: 200, // 200MB
      maxImages: 2000,
      ttl: 12 * 60 * 60, // 12 horas
      evictionStrategy: 'lru'
    });

    if (import.meta.env.DEV) {
      console.log('✅ Serviços de upload inicializados');
      console.log('🧠 Cache inteligente ativo');
      console.log('📊 Monitoramento de storage iniciado');
    }
  }
}
